import {
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Activity,
  ChevronRight
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter
} from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { TabsContent } from "../../../components/ui/tabs";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";

const OverviewTab = ({
  stats,
  dynamicChartData,
  timeframe,
  setTimeframe,
  activeJobs,
  recentActivities
}) => {
  const { t } = useTranslation();
  return (
    <TabsContent value="overview" className="mt-0 space-y-8 pb-12 outline-none">
      {/* STATS GRID */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat, idx) => (
          <Card key={idx} className="border shadow-none rounded-xl bg-white dark:bg-slate-800 overflow-hidden transition-all hover:bg-slate-50/50 dark:hover:bg-slate-700/50">
            <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
              <span className="text-[13px] font-semibold text-muted-foreground uppercase tracking-tight">{stat.label}</span>
              {stat.growth && (
                <div className={`flex items-center space-x-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${
                  stat.isNegative
                    ? "bg-red-100/50 text-red-600"
                    : "bg-emerald-100/50 text-emerald-600"
                }`}>
                  {stat.isNegative ? <TrendingDown size={10} /> : <TrendingUp size={10} />}
                  <span>{stat.growth}</span>
                </div>
              )}
            </CardHeader>
            <CardContent className="pt-2 pb-6">
              <div className="text-3xl font-extrabold tracking-tight text-foreground">{stat.value}</div>
            </CardContent>
            <CardFooter className="pt-0 pb-4 flex flex-col items-start border-t border-slate-50 dark:border-slate-700 bg-slate-50/30 dark:bg-slate-800/50">
               <span className={`text-[11px] font-medium mt-3 flex items-center ${stat.isNegative ? "text-red-500" : "text-emerald-500"}`}>
                {stat.trend}
                {stat.isNegative ? <TrendingDown size={12} className="ml-1" /> : <TrendingUp size={12} className="ml-1" />}
               </span>
               <span className="text-[10px] text-muted-foreground mt-0.5">{stat.subtext}</span>
            </CardFooter>
          </Card>
        ))}
      </div>

      {/* MAIN CHART SECTION */}
      <Card className="border shadow-none rounded-2xl overflow-hidden bg-white dark:bg-slate-800">
        <CardHeader className="flex flex-row items-center justify-between pb-8">
          <div>
            <CardTitle className="text-lg font-bold">{t("adminDashboard.overview.activityChart.title")}</CardTitle>
            <CardDescription className="text-xs">{t("adminDashboard.overview.activityChart.description")}</CardDescription>
          </div>
          <div className="flex bg-slate-100 dark:bg-slate-700 p-1 rounded-lg">
            <button
              onClick={() => setTimeframe("last-3-months")}
              className={`px-3 py-1.5 text-[11px] font-bold rounded-md transition-all ${timeframe === "last-3-months" ? "bg-white dark:bg-slate-600 text-slate-900 dark:text-white shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"}`}
            >
              {t("adminDashboard.overview.timeframe.threeMonths")}
            </button>
            <button
              onClick={() => setTimeframe("last-30-days")}
              className={`px-3 py-1.5 text-[11px] font-bold rounded-md transition-all ${timeframe === "last-30-days" ? "bg-white dark:bg-slate-600 text-slate-900 dark:text-white shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"}`}
            >
              {t("adminDashboard.overview.timeframe.thirtyDays")}
            </button>
            <button
              onClick={() => setTimeframe("last-7-days")}
              className={`px-3 py-1.5 text-[11px] font-bold rounded-md transition-all ${timeframe === "last-7-days" ? "bg-white dark:bg-slate-600 text-slate-900 dark:text-white shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"}`}
            >
              {t("adminDashboard.overview.timeframe.sevenDays")}
            </button>
          </div>
        </CardHeader>
        <CardContent className="h-[350px] px-8 pb-10">
           <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dynamicChartData}>
                 <defs>
                    <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                       <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.1}/>
                       <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                    </linearGradient>
                 </defs>
                 <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                 <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={{fontSize: 10, fill: '#94a3b8'}}
                    tickFormatter={(str) => {
                        const d = new Date(str);
                        return d.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' });
                    }}
                    minTickGap={30}
                 />
                 <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{fontSize: 10, fill: '#94a3b8'}}
                    allowDecimals={false}
                 />
                 <Tooltip
                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)', fontSize: '12px' }}
                    labelStyle={{ fontWeight: 'bold', marginBottom: '4px' }}
                    formatter={(value) => [value, t("adminDashboard.overview.activityChart.scans")]}
                    labelFormatter={(label) => new Date(label).toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' })}
                 />
                 <Area
                    type="monotone"
                    dataKey="count"
                    stroke="hsl(var(--primary))"
                    strokeWidth={3}
                    fillOpacity={1}
                    fill="url(#colorCount)"
                    animationDuration={1500}
                 />
              </AreaChart>
           </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* LOWER GRID: RECENT JOBS & ACTIVITY */}
      <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-7">
        <Card className="lg:col-span-4 border shadow-none bg-white dark:bg-slate-800 rounded-2xl overflow-hidden">
          <CardHeader className="pb-4 border-b border-slate-50 dark:border-slate-700">
            <CardTitle className="text-base font-bold">{t("adminDashboard.overview.recentJobs.title")}</CardTitle>
            <CardDescription className="text-xs">{t("adminDashboard.overview.recentJobs.description")}</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-50 dark:divide-slate-700">
              {activeJobs.length > 0 ? (
                activeJobs.slice(0, 5).map((job) => (
                  <div key={job._id} className="flex items-center p-4 hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors group">
                    <div className="h-10 w-10 rounded-xl bg-primary/5 dark:bg-primary/10 flex items-center justify-center text-primary mr-4 group-hover:scale-110 transition-transform">
                      <Activity size={18} />
                    </div>
                    <div className="flex-1 space-y-0.5">
                      <p className="text-sm font-bold text-slate-800 dark:text-white">
                        {job.user?.username || t("adminDashboard.overview.recentJobs.guestReconstruction")}
                      </p>
                      <div className="flex items-center text-[11px] text-muted-foreground font-medium">
                        <span className="text-slate-400 dark:text-slate-500 capitalize">{job.title || t("adminDashboard.overview.recentJobs.scan")}</span>
                        <span className="mx-2 opacity-30">•</span>
                        <span>{new Date(job.date).toLocaleDateString('de-DE')}</span>
                      </div>
                    </div>
                    <Badge
                      variant={job.status === 'ready' ? 'success' : job.status === 'error' ? 'destructive' : 'secondary'}
                      className="rounded-lg text-[10px] font-bold px-2 py-0.5 h-auto capitalize"
                    >
                      {job.status}
                    </Badge>
                  </div>
                ))
              ) : (
                <div className="p-12 text-center text-muted-foreground font-medium text-sm">
                  {t("adminDashboard.overview.recentJobs.noActiveJobs")}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-3 border shadow-none bg-white dark:bg-slate-800 rounded-2xl overflow-hidden">
          <CardHeader className="pb-4 border-b border-slate-50 dark:border-slate-700">
            <CardTitle className="text-base font-bold">{t("adminDashboard.overview.activityFeed.title")}</CardTitle>
            <CardDescription className="text-xs">{t("adminDashboard.overview.activityFeed.description")}</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-50 dark:divide-slate-700">
              {recentActivities.length > 0 ? (
                recentActivities.map((act, idx) => (
                  <div key={idx} className="flex items-center p-4 hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors group">
                    <div className="h-10 w-10 rounded-xl bg-[#0f7699]/10 dark:bg-[#4fc3f7]/10 flex items-center justify-center text-[#0f7699] dark:text-[#4fc3f7] mr-4 group-hover:bg-[#0f7699]/20 dark:group-hover:bg-[#4fc3f7]/20 transition-colors">
                      <TrendingUp size={18} />
                    </div>
                    <div className="flex-1 space-y-0.5">
                      <p className="text-sm font-bold text-slate-800 dark:text-white line-clamp-1">{act.title || t("adminDashboard.overview.activityFeed.activity")}</p>
                      <p className="text-[11px] text-muted-foreground font-medium">{act.username} • {new Date(act.date).toLocaleTimeString('de-DE', {hour: '2-digit', minute:'2-digit'})} {t("adminDashboard.overview.activityFeed.time")}</p>
                    </div>
                    <ChevronRight size={14} className="text-slate-300 group-hover:text-primary transition-colors" />
                  </div>
                ))
              ) : (
                <div className="p-12 text-center text-muted-foreground">
                  {t("adminDashboard.overview.activityFeed.noActivities")}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </TabsContent>
  );
};

export default OverviewTab;
